/** Right-side drawer with full token detail + reveal / revoke / extend. */
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Drawer,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import { ConsoleApiError } from '@/api/client';
import * as sessionsApi from '@/api/sessions';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { useSnackbar } from '@/components/Snackbar';
import { ValueActions } from '@/components/ValueActions/ValueActions';
import { UserKindChip } from '@/features/users/UserKindChip';
import { usePermissions } from '@/hooks/usePermissions';
import { formatDate } from '@/utils/format';

import {
  authActions,
  bookingActions,
  createdActions,
  expiresActions,
  kindActions,
  scopeActions,
  sessionIdActions,
  systemTenantActions,
  tenantActions,
  userActions,
} from './sessionLinks';

const EXTEND_OPTIONS: { label: string; seconds: number }[] = [
  { label: '+1 hour', seconds: 3600 },
  { label: '+8 hours', seconds: 8 * 3600 },
  { label: '+1 day', seconds: 24 * 3600 },
  { label: '+7 days', seconds: 7 * 24 * 3600 },
  { label: '+30 days', seconds: 30 * 24 * 3600 },
];

interface Props {
  sessionId: number;
  open: boolean;
  onClose: () => void;
  onChanged?: () => void;
}

export function SessionDetailDrawer({ sessionId, open, onClose, onChanged }: Props) {
  const qc = useQueryClient();
  const snackbar = useSnackbar();
  const { hasPerm } = usePermissions();
  const [revealed, setRevealed] = useState(false);
  const [confirmRevoke, setConfirmRevoke] = useState(false);
  const [extendSeconds, setExtendSeconds] = useState(EXTEND_OPTIONS[1]!.seconds);

  const detailQuery = useQuery({
    queryKey: ['session', sessionId, revealed],
    queryFn: () => sessionsApi.retrieve(sessionId, revealed),
    enabled: open,
  });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['sessions'] });
    onChanged?.();
  };

  const revokeMutation = useMutation({
    mutationFn: () => sessionsApi.revoke(sessionId),
    onSuccess: () => {
      snackbar.show('Session revoked.', 'success');
      invalidate();
      onClose();
    },
    onError: (e: unknown) =>
      snackbar.show(e instanceof ConsoleApiError ? e.message : 'Revoke failed.', 'error'),
  });

  const extendMutation = useMutation({
    mutationFn: () => sessionsApi.extend(sessionId, extendSeconds),
    onSuccess: () => {
      snackbar.show('Session extended.', 'success');
      qc.invalidateQueries({ queryKey: ['session', sessionId] });
      invalidate();
    },
    onError: (e: unknown) =>
      snackbar.show(e instanceof ConsoleApiError ? e.message : 'Extend failed.', 'error'),
  });

  const s = detailQuery.data;

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      // The AppBar sits at zIndex.drawer+1; temporary drawers default to
      // zIndex.drawer, so without this the app bar covers the drawer top.
      sx={{ zIndex: (t) => t.zIndex.modal }}
    >
      <Box sx={{ width: 420, p: 2 }} role="presentation">
        <Stack direction="row" alignItems="center" sx={{ mb: 2 }}>
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            Session{' '}
            <ValueActions actions={sessionIdActions(sessionId)}>#{sessionId}</ValueActions>
          </Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Stack>

        {detailQuery.isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
            <CircularProgress />
          </Box>
        )}
        {detailQuery.isError && <Alert severity="error">Couldn’t load session.</Alert>}

        {s && (
          <Stack spacing={1.5}>
            <Field label="User">
              <ValueActions actions={userActions(s.user)}>
                <RouterLink to={`/users/${s.user.id}`}>{s.user.email}</RouterLink>
              </ValueActions>{' '}
              <ValueActions chip actions={kindActions(s.user.kind)}>
                <UserKindChip kind={s.user.kind} sx={{ ml: 0.5 }} />
              </ValueActions>
            </Field>
            <Field label="Tenant">
              {s.tour_operator ? (
                <ValueActions actions={tenantActions(s.tour_operator)}>
                  <RouterLink to={`/tour-operators/${s.tour_operator.id}`}>
                    {s.tour_operator.code
                      ? `${s.tour_operator.name} (${s.tour_operator.code})`
                      : s.tour_operator.name}
                  </RouterLink>
                </ValueActions>
              ) : (
                <ValueActions actions={systemTenantActions()}>
                  <span style={{ color: '#888' }}>system (no tenant)</span>
                </ValueActions>
              )}
            </Field>
            <Field label="Scope">
              <ValueActions chip actions={scopeActions(s.access.scope ?? s.access.flags.join('+'))}>
                <Chip size="small" label={s.access.scope ?? s.access.flags.join('+')} />
              </ValueActions>
            </Field>
            <Field label="Auth type">
              <ValueActions actions={authActions(s.auth, s.auth_display)}>{s.auth_display}</ValueActions>
            </Field>
            <Field label="Created">
              <ValueActions actions={createdActions(s.created)}>{formatDate(s.created)}</ValueActions>
            </Field>
            <Field label="Expires">
              {s.expires ? (
                <ValueActions actions={expiresActions(s.expires)}>{formatDate(s.expires)}</ValueActions>
              ) : (
                '—'
              )}
              {s.is_expired && <Chip size="small" color="error" label="expired" sx={{ ml: 1 }} />}
            </Field>
            <Field label="TTL">
              {s.ttl_seconds !== null ? `${Math.round(s.ttl_seconds / 3600)}h` : '—'}
            </Field>
            {s.booking && (
              <Field label="Booking">
                <ValueActions actions={bookingActions(s.booking)}>
                  {s.booking.label || `#${s.booking.id}`}
                </ValueActions>
              </Field>
            )}

            <Divider />

            <Field label={s.token.is_fingerprint ? 'Fingerprint' : 'Token value'}>
              <Stack direction="row" spacing={0.5} alignItems="center">
                <code style={{ fontSize: 12, wordBreak: 'break-all' }}>{s.token.value}</code>
                <Tooltip title="Copy">
                  <IconButton
                    size="small"
                    onClick={() => {
                      void navigator.clipboard?.writeText(s.token.value);
                      snackbar.show('Copied.', 'success');
                    }}
                  >
                    <ContentCopyIcon fontSize="inherit" />
                  </IconButton>
                </Tooltip>
              </Stack>
            </Field>
            {s.token.is_fingerprint && hasPerm('console_session_reveal') && (
              <Button
                size="small"
                startIcon={<VisibilityIcon />}
                onClick={() => setRevealed(true)}
                sx={{ alignSelf: 'flex-start' }}
              >
                Reveal raw token
              </Button>
            )}
            {!s.token.is_fingerprint && (
              <Alert severity="warning" sx={{ py: 0 }}>
                Raw token revealed — this action was recorded in the audit log.
              </Alert>
            )}

            <Divider />

            {hasPerm('console_session_manage') && (
              <>
                <Stack direction="row" spacing={1} alignItems="center">
                  <TextField
                    select
                    size="small"
                    label="Extend by"
                    value={extendSeconds}
                    onChange={(e) => setExtendSeconds(Number(e.target.value))}
                    sx={{ flexGrow: 1 }}
                  >
                    {EXTEND_OPTIONS.map((o) => (
                      <MenuItem key={o.seconds} value={o.seconds}>
                        {o.label}
                      </MenuItem>
                    ))}
                  </TextField>
                  <Button
                    variant="outlined"
                    onClick={() => extendMutation.mutate()}
                    disabled={extendMutation.isPending}
                  >
                    Extend
                  </Button>
                </Stack>
                <Button
                  color="error"
                  variant="contained"
                  onClick={() => setConfirmRevoke(true)}
                  sx={{ alignSelf: 'flex-start' }}
                >
                  Revoke session
                </Button>
              </>
            )}
          </Stack>
        )}
      </Box>

      {confirmRevoke && (
        <ConfirmDialog
          open
          title="Revoke this session?"
          message="The token will be deleted and the holder signed out on that device immediately."
          confirmLabel="Revoke"
          destructive
          onConfirm={() => {
            setConfirmRevoke(false);
            revokeMutation.mutate();
          }}
          onCancel={() => setConfirmRevoke(false)}
        />
      )}
    </Drawer>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary" display="block">
        {label}
      </Typography>
      <Typography variant="body2" component="div">
        {children}
      </Typography>
    </Box>
  );
}
